import { IconProp } from '@fortawesome/fontawesome-svg-core';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { v4 } from 'uuid';

import {
  FileVersionExportCompletedMessage,
  FileVersionExportUpdatedMessage,
} from '../../../../../../../../../typings/websocket-messages';
import { SkyMapAxiosServiceFactory } from '../../../../../../../services/axios/skymap-axios-service-factory';
import { WebApi } from '../../../../../../../services/web-api';
import { iiafe } from '../../../../../../../utils/async';
import { isDefined } from '../../../../../../../utils/variables';
import { WebsocketManager } from '../../../../../../../utils/websocket/websocket-manager';
import { AssetVisibilityManager } from '../../../../../../../viewer/asset-visibility-manager';
import { CoordinateManager } from '../../../../../../../viewer/coordinate-manager';
import {
  appendResultToDesignNode,
  generateDesignNodes,
  generateDesignNodesFromAsset,
} from '../../../../../../../viewer/elements/design/serialize';
import { Dxf } from '../../../../../../../viewer/elements/dxf';
import { AssetType, ElementObj } from '../../../../../../../viewer/elements/element-obj';
import { Glb } from '../../../../../../../viewer/elements/glb';
import { GltfLoader } from '../../../../../../../viewer/loader/gltf';
import { publish, SubscriptionTopic } from '../../../../../../../viewer/messaging/pubsub';
import { DesignTool } from '../../../../../../../viewer/tools/design-tool';
import { importFunctions } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/commands/features/import-geometry';
import { useAssets } from '../../../../../../hooks/use-assets';
import { useErrorHandling } from '../../../../../../hooks/use-error-handling';
import { useSubscribe } from '../../../../../../hooks/use-subscribe';
import { Button } from '../../../../../button/button';
import { CheckBox } from '../../../../../check-box/check-box';
import { DropdownSuggestion } from '../../../../../dropdown-suggestion/dropdown-suggesion';
import { FileSelectionButton } from '../../../../../file-selection-button/file-selection-button';
import { Icon } from '../../../../../icon/icon';
import { InfoBox } from '../../../../../info-box/info-box';
import { LabelledContainer } from '../../../../../labelled-container/labelled-container';
import {
  ProgressDialog,
  useProgressDialogDependencies,
} from '../../../../../progress-dialog/progress-dialog';
import { Select } from '../../../../../select/select';
import { useAssetChangedEvent } from '../../../../../skyview-new/hooks/use-asset-changed-event';
import { Stack } from '../../../../../stack/stack';

type Props = {
  tool: DesignTool;
};

enum Status {
  Idle,
  Error,
}

function getAssetTypeIcon(type: AssetType): IconProp {
  switch (type) {
    case 'design':
      return ['fal', 'pen-ruler'];
    case 'area':
      return ['fal', 'draw-polygon'];
    case 'line':
      return ['fal', 'wave-triangle'];
    case 'poi':
      return ['fal', 'location-dot'];
    case 'dxf':
    case 'glb':
      return ['fal', 'cubes'];
    case 'model':
      return ['fal', 'cube'];
    case 'terrain-model':
      return ['fal', 'mountains'];
  }
}

type Option = {
  id: string;
  name: string | { filterName: string; node: React.ReactNode };
  data:
    | {
        type: 'import';
      }
    | {
        type: 'asset';
        asset: ElementObj;
      };
};

function isRowValid(row: string[], northingRow: number, eastingRow: number, heightRow: number) {
  try {
    importFunctions.parseCsvRowCoordinate(row, northingRow, eastingRow, heightRow);
    return true;
  } catch {
    return false;
  }
}

export function useSubscribeToExportCreatedMessage(
  fileName: string,
  callback: (event: FileVersionExportCompletedMessage | FileVersionExportUpdatedMessage) => void,
) {
  useSubscribe(SubscriptionTopic.WebsocketMessageRecieved, (event) => {
    if (event.message === 'FileVersionExportUpdated' && event.fileName === fileName) {
      callback(event);
    }
  });
}

const ImportGeometry = (props: Props) => {
  const { t } = useTranslation();

  const { handleError, buildErrorList } = useErrorHandling({ displayNonApiErrors: true });
  const [status, setStatus] = useState(Status.Idle);
  const [createAsLine, setCreateAsLine] = useState(false);
  const [fileName, setFileName] = useState('');
  const [exportFilename, setExportFilename] = useState('');
  const [result, setResult] = useState<string[][]>();
  const [columns, setColumns] = useState<{ id: string; name: string }[]>([]);
  const [validRows, setValidRows] = useState<string[][]>([]);
  const progress = useProgressDialogDependencies(1);

  const [northingRow, setNorthingRow] = useState(0);
  const [eastingRow, setEastingRow] = useState(1);
  const [heightRow, setHeightRow] = useState(2);
  const [asset, setAsset] = useState<ElementObj>();
  const [isAssetVisible, setIsAssetVisible] = useState(true);

  const { assets } = useAssets<ElementObj>([
    'design',
    'dxf',
    'area',
    'line',
    'glb',
    'terrain-model',
    'model',
  ]);
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<'asset' | 'file'>('asset');

  const { assetChangedEvent } = useAssetChangedEvent(asset);

  const onFileVersionExported = useCallback(
    (event: FileVersionExportCompletedMessage | FileVersionExportUpdatedMessage) => {
      iiafe(async () => {
        try {
          const option = options[Number(selectedOptionId)];

          if (!isDefined(option) || option.data.type !== 'asset') {
            throw new Error('Invalid option selected.');
          }

          if (event.status !== 'COMPLETED') {
            throw new Error('No url in export result.');
          }

          const arrayBuffer = await WebApi.fetchS3DataAsArrayBuffer(event.downloadUrl);
          const gltf = await GltfLoader.instance.loadGltf(Buffer.from(arrayBuffer));

          // Save and zero the root node's position before generating design nodes.
          // The DesignNode constructor calls localToWorld() which bakes parent
          // transforms into every vertex. With the root position zeroed, vertices
          // stay in local coordinates (small values) avoiding floating-point
          // precision issues from large absolute project coordinates.
          const root = gltf.scene.children[0];
          if (!root) {
            throw new Error('Invalid GLB export: scene has no root child node.');
          }
          const rootPosition = root.position.clone();
          root.position.set(0, 0, 0);

          const result = generateDesignNodes(gltf.scene);

          result.transformationMatrix.setPosition(
            CoordinateManager.getInstance().projectCoordsToLocal({
              easting: rootPosition.x,
              northing: rootPosition.y,
              height: rootPosition.z,
            }),
          );

          await appendResultToDesignNode(
            props.tool,
            result,
            progress.controller.current,
            progress.abortController.current,
          );
          setSelectedOptionId(undefined);
          setAsset(undefined);
          setIsAssetVisible(false);
          await AssetVisibilityManager.getInstance().toggleAsset(option.data.asset, false, false);
        } catch (err: unknown) {
          handleError(
            new Error(
              t('section.functions.import.content.importObjectError', {
                ns: 'skyviewDesign',
              }) as string,
            ),
          );
        } finally {
          setLoading(false);
          progress.setShowDialog(false);
        }
      });
    },
    [selectedOptionId, handleError, t, options, progress, props.tool],
  );

  useSubscribeToExportCreatedMessage(exportFilename, onFileVersionExported);

  React.useEffect(() => {
    if (!isDefined(asset)) {
      setLoading(false);
      setIsAssetVisible(true);
      return;
    }

    const visible = asset.getStateVisibility() && asset.loadingStatus !== 'loading';
    setIsAssetVisible(visible);

    if (!visible) {
      void AssetVisibilityManager.getInstance().toggleAsset(asset.uuid, true, false);
    }
  }, [asset, assetChangedEvent]);

  useEffect(() => {
    const newOptions: Option[] = assets.map((x, idx) => ({
      id: idx.toString(),
      name: {
        filterName: x.name,
        node: (
          <OptionNode title={x.name}>
            <Icon fixedWidth={true} icon={getAssetTypeIcon(x.assetType)} />
            <OptionName>{x.name}</OptionName>
          </OptionNode>
        ),
      },
      data: {
        type: 'asset',
        asset: x,
      },
    }));

    setOptions(newOptions);
    setSelectedOptionId(undefined);
  }, [assets]);

  useEffect(() => {
    if (!isDefined(result)) {
      setValidRows([]);
      setColumns([]);
      return;
    }

    setColumns(
      result[0].map((col, idx) => {
        return {
          id: idx.toString(),
          name: col,
        };
      }),
    );

    const rows: string[][] = [];
    for (const row of result) {
      if (isRowValid(row, northingRow, eastingRow, heightRow)) {
        rows.push(row);
      }
    }

    setValidRows(rows);
  }, [result, northingRow, eastingRow, heightRow]);

  const onImportViaExport = async (asset: Dxf | Glb) => {
    try {
      setLoading(true);

      progress.controller.current.callback(
        0,
        t('section.functions.import.content.preparing', {
          ns: 'skyviewDesign',
        }),
      );
      if (!isDefined(asset.selectedVersion)) {
        throw new Error('No version selected.');
      }

      const exportFileName = `${v4()}.glb`;

      setExportFilename(exportFileName);

      await WebsocketManager.instance.waitForWebsocketConnection();
      await SkyMapAxiosServiceFactory.instance.createFileVersionServiceV1().createExport({
        path: {
          fileVersionId: asset.selectedVersion.id,
        },
        body: {
          saveResult: false,
          fileName: exportFileName,
          options: {
            type: '.glb',
          },
        },
      });

      progress.controller.current.callback(
        1,
        t('section.functions.import.content.waitForExport', {
          ns: 'skyviewDesign',
        }),
      );

      return;
    } catch (err: unknown) {
      setLoading(false);
      progress.setShowDialog(false);

      publish(SubscriptionTopic.ToastrMessage, {
        type: 'error',
        message: t('section.functions.import.content.importObjectError', {
          ns: 'skyviewDesign',
        }),
      });
    }
  };

  const onImportAsset = async (asset: ElementObj) => {
    try {
      setLoading(true);

      const result = generateDesignNodesFromAsset(asset);
      await appendResultToDesignNode(
        props.tool,
        result,
        progress.controller.current,
        progress.abortController.current,
      );
      setSelectedOptionId(undefined);
      setAsset(undefined);
      setIsAssetVisible(false);
      await AssetVisibilityManager.getInstance().toggleAsset(asset, false, false);
    } catch (err: unknown) {
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'error',
        message: t('section.functions.import.content.importObjectError', {
          ns: 'skyviewDesign',
        }),
      });
    } finally {
      setLoading(false);
      progress.setShowDialog(false);
    }
  };

  return (
    <Content>
      <TabBar>
        <TabButton
          $active={activeTab === 'asset'}
          onClick={() => {
            setActiveTab('asset');
            setResult(undefined);
            setStatus(Status.Idle);
          }}
        >
          {t('section.functions.import.tabs.asset', { ns: 'skyviewDesign' })}
        </TabButton>
        <TabButton
          $active={activeTab === 'file'}
          onClick={() => {
            setActiveTab('file');
            setSelectedOptionId(undefined);
            setAsset(undefined);
          }}
        >
          {t('section.functions.import.tabs.file', { ns: 'skyviewDesign' })}
        </TabButton>
      </TabBar>

      {activeTab === 'asset' && (
        <>
          <DropdownSuggestion
            disabled={!isAssetVisible || loading}
            placeholder={t('section.functions.import.content.searchPlaceholder', {
              ns: 'skyviewDesign',
            })}
            portalToBody={true}
            suggestions={options}
            value={selectedOptionId}
            width={'100%'}
            onSuggestionSelected={(suggestion) => {
              const id = suggestion.id;
              setSelectedOptionId(id);
              const option = options[Number(id)];
              if (isDefined(option) && option.data.type === 'asset') {
                setAsset(option.data.asset);
              } else {
                setAsset(undefined);
              }
            }}
          />

          {buildErrorList()}

          {isDefined(selectedOptionId) && (
            <Button
              color="primary"
              disabled={!isAssetVisible || loading}
              loading={loading}
              variant="contained"
              onClick={async () => {
                progress.setShowDialog(true);
                const option = options[Number(selectedOptionId)];
                if (isDefined(option) && option.data.type === 'asset') {
                  if (
                    option.data.asset instanceof Dxf ||
                    (option.data.asset instanceof Glb &&
                      option.data.asset.getFileType() === 'LandXml')
                  ) {
                    await onImportViaExport(option.data.asset);
                  } else {
                    await onImportAsset(option.data.asset);
                  }
                }
              }}
            >
              {t('section.functions.import.content.importObjectButton', { ns: 'skyviewDesign' })}
            </Button>
          )}
        </>
      )}

      {activeTab === 'file' && (
        <>
          {!isDefined(result) && (
            <>
              <InfoBox color="yellow">
                {t('section.functions.import.content.info', { ns: 'skyviewDesign' })}
              </InfoBox>

              {status === Status.Error && (
                <InfoBox color="red" leftIcon={{ icon: 'exclamation-triangle' }}>
                  {t('section.functions.import.content.readCsvError', { ns: 'skyviewDesign' })}
                </InfoBox>
              )}

              <FileSelectionButton
                accept=".csv,.txt"
                color="primary"
                leftIcon={{ icon: ['fad', 'upload'] }}
                text={t('section.functions.import.content.chooseFile', {
                  ns: 'skyviewDesign',
                })}
                variant="contained"
                onFilesSelected={(files) => {
                  try {
                    const reader = new FileReader();
                    reader.onload = (e: ProgressEvent<FileReader>) => {
                      try {
                        const parsedPoints = importFunctions.parseCsv(e.target?.result as string, {
                          minColumns: 3,
                        });

                        if (parsedPoints.length === 0) {
                          setStatus(Status.Error);
                          setResult(undefined);
                        } else {
                          const firstRow = parsedPoints[0];
                          const coordinateOffset = firstRow.length > 3 ? 1 : 0;

                          const nRow = 0 + coordinateOffset;
                          const eRow = 1 + coordinateOffset;
                          const hRow = 2 + coordinateOffset;

                          if (isRowValid(firstRow, nRow, eRow, hRow)) {
                            setNorthingRow(nRow);
                            setEastingRow(eRow);
                            setHeightRow(hRow);
                          } else {
                            setNorthingRow(
                              firstRow.findIndex((x) =>
                                ['northing', 'x'].includes(x.toLowerCase()),
                              ) ?? nRow,
                            );
                            setEastingRow(
                              firstRow.findIndex((x) =>
                                ['easting', 'y'].includes(x.toLowerCase()),
                              ) ?? eRow,
                            );
                            setHeightRow(
                              firstRow.findIndex((x) =>
                                ['height', 'z'].includes(x.toLowerCase()),
                              ) ?? hRow,
                            );
                          }

                          setResult(parsedPoints);
                        }
                      } catch {
                        setResult(undefined);
                        setStatus(Status.Error);
                      }
                    };

                    reader.onerror = () => setStatus(Status.Error);
                    reader.readAsText(files[0]);
                    setFileName(files[0].name);
                  } catch (err) {
                    setResult(undefined);
                    setStatus(Status.Error);
                  }
                }}
              />
            </>
          )}

          {isDefined(result) && (
            <>
              {validRows.length === 0 ? (
                <InfoBox color="red" leftIcon={{ icon: 'exclamation-triangle' }} title={fileName}>
                  {t('section.functions.import.content.readCsvNoValidPoints', {
                    ns: 'skyviewDesign',
                  })}
                </InfoBox>
              ) : (
                <InfoBox color="green" leftIcon={{ icon: 'check' }} title={fileName}>
                  {t('section.functions.import.content.readCsvValidPoints', {
                    ns: 'skyviewDesign',
                    count: validRows.length,
                  })}
                </InfoBox>
              )}

              <CheckBox checked={createAsLine} onChange={(e) => setCreateAsLine(e.target.checked)}>
                {t('section.functions.import.content.importPointsAsLine', { ns: 'skyviewDesign' })}
              </CheckBox>

              <LabelledContainer text="N">
                <Select
                  options={columns}
                  value={northingRow.toString()}
                  onChange={(e) => setNorthingRow(Number(e.target.value))}
                />
              </LabelledContainer>
              <LabelledContainer text="E">
                <Select
                  options={columns}
                  value={eastingRow.toString()}
                  onChange={(e) => setEastingRow(Number(e.target.value))}
                />
              </LabelledContainer>
              <LabelledContainer text="H">
                <Select
                  options={columns}
                  value={heightRow.toString()}
                  onChange={(e) => setHeightRow(Number(e.target.value))}
                />
              </LabelledContainer>

              <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                <Button
                  color="primary"
                  disabled={validRows.length === 0}
                  variant="contained"
                  onClick={() => {
                    const headers = isRowValid(result[0], northingRow, eastingRow, heightRow)
                      ? result[0].map((_, idx) => {
                          return t('section.functions.import.content.importAttributeName', {
                            ns: 'skyviewDesign',
                            index: idx + 1,
                            fileName,
                          }) as string;
                        })
                      : result[0];

                    importFunctions.applyCsvPoints(
                      props.tool,
                      headers,
                      validRows,
                      northingRow,
                      eastingRow,
                      heightRow,
                      createAsLine,
                    );
                    setResult(undefined);
                  }}
                >
                  {t('confirm', { ns: 'common' })}
                </Button>
                <Button variant="text" onClick={() => setResult(undefined)}>
                  {t('cancel', { ns: 'common' })}
                </Button>
              </Stack>
            </>
          )}
        </>
      )}

      {progress.showDialog && (
        <ProgressDialog
          abortController={progress.abortController.current}
          progressController={progress.controller.current}
          title={t('section.functions.import.content.importObjectDialog.title', {
            ns: 'skyviewDesign',
          })}
          onClose={() => {
            progress.setShowDialog(false);
          }}
        />
      )}
    </Content>
  );
};

const TabBar = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray.light};
`;

const TabButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.4em 0.5em;
  font-size: 0.8em;
  border: none;
  border-bottom: 2px solid
    ${({ $active, theme }) => ($active ? theme.color.brand.dark : 'transparent')};
  background: transparent;
  color: ${({ theme }) => theme.color.gray.dark};
  cursor: pointer;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  transition:
    color 0.15s,
    border-color 0.15s;
`;

const OptionNode = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-size: 0.8em;
  overflow: hidden;
  min-width: 0;
`;

const OptionName = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;

  section {
    display: flex;
    flex-direction: row;
    gap: 1em;
    justify-content: space-between;
    * {
      width: 100%;
    }
  }

  > label {
    width: 100%;
  }
`;

export { ImportGeometry };
